import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { resolveAssetPath } from '../utils/assetPath'

const PageContainer = styled.div`
  display: flex;
  gap: 32px;
  padding: 48px 64px;
  color: #21313c;
  background: #2b3d2b;
  min-height: calc(100vh - 54px);
  box-sizing: border-box;
`

const Panel = styled.div`
  background: rgba(255, 255, 255, 0.96);
  border-radius: 18px;
  padding: 28px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.25);
`

const TripListPanel = styled(Panel)`
  width: 320px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const TripsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: calc(100vh - 320px);
  overflow-y: auto;
  padding-right: 4px;
`

const TripListItem = styled.button`
  border: 1px solid
    ${({ $active }) => ($active ? '#4c3de3' : 'rgba(25, 42, 86, 0.2)')};
  background: ${({ $active }) => ($active ? '#4c3de3' : 'transparent')};
  color: ${({ $active }) => ($active ? '#ffffff' : '#0f1d37')};
  border-radius: 12px;
  padding: 14px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  span {
    display: block;
    font-size: 11px;
    font-weight: 500;
    margin-top: 6px;
    color: ${({ $active }) => ($active ? '#f3f4ff' : '#415266')};
  }
`

const AddTripButton = styled.button`
  border: dashed 2px rgba(76, 61, 227, 0.4);
  background: rgba(76, 61, 227, 0.08);
  color: #4c3de3;
  border-radius: 12px;
  padding: 16px 18px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s ease;

  :hover {
    background: rgba(76, 61, 227, 0.15);
  }
`

const GlobalSwitch = styled.label`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 12px;
  background: ${({ $active }) =>
    $active ? 'rgba(20, 160, 90, 0.15)' : 'rgba(15, 29, 55, 0.06)'};
  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(20, 160, 90, 0.35)' : 'transparent')};
`

const ToggleInput = styled.input`
  width: 44px;
  height: 24px;
`

const ToggleLabel = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: ${({ $active }) => ($active ? '#1a5a33' : '#1e2e3d')};
`

const FormPanel = styled(Panel)`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  color: #0f1d37;
`

const PanelActions = styled.div`
  display: flex;
  gap: 12px;
`

const ActionButton = styled.button`
  padding: 11px 20px;
  border-radius: 999px;
  border: ${({ $variant }) =>
    $variant === 'primary' ? 'none' : '1px solid rgba(15, 29, 55, 0.2)'};
  background: ${({ $variant }) =>
    $variant === 'primary' ? '#4c3de3' : 'transparent'};
  color: ${({ $variant }) => ($variant === 'primary' ? '#ffffff' : '#21313c')};
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;

  :hover {
    opacity: 0.92;
  }
`

const DeleteButton = styled(ActionButton)`
  border: 1px solid rgba(197, 48, 48, 0.3);
  color: #c53030;
`

const Fieldset = styled.fieldset`
  border: 1px solid rgba(15, 29, 55, 0.12);
  border-radius: 14px;
  padding: 20px 22px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const Legend = styled.legend`
  padding: 0 10px;
  font-weight: 700;
  font-size: 13px;
  color: #132b50;
`

const FieldGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-weight: 600;
  font-size: 12px;
  color: #21313c;
`

const Input = styled.input`
  border: 1px solid rgba(15, 29, 55, 0.18);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  background: #ffffff;
  transition: border 0.2s ease;

  :focus {
    border-color: #4c3de3;
    box-shadow: 0 0 0 2px rgba(76, 61, 227, 0.1);
    outline: none;
  }
`

const TextArea = styled.textarea`
  border: 1px solid rgba(15, 29, 55, 0.18);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  resize: vertical;
  min-height: 72px;

  :focus {
    border-color: #4c3de3;
    box-shadow: 0 0 0 2px rgba(76, 61, 227, 0.1);
    outline: none;
  }
`

const EmptyState = styled.div`
  padding: 60px 24px;
  text-align: center;
  color: rgba(15, 29, 55, 0.6);
  font-weight: 600;
`

const PreviewRow = styled.div`
  display: flex;
  gap: 20px;
  align-items: stretch;
  flex-wrap: wrap;
`

const PreviewImage = styled.img`
  width: 220px;
  height: 140px;
  object-fit: cover;
  border-radius: 14px;
  border: 1px solid rgba(15, 29, 55, 0.15);
  background: #f4f6f8;
`

const PreviewPlaceholder = styled.div`
  width: 220px;
  height: 140px;
  border-radius: 14px;
  border: 1px dashed rgba(15, 29, 55, 0.2);
  background: rgba(240, 244, 247, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  color: rgba(15, 29, 55, 0.6);
  font-weight: 600;
  font-size: 12px;
  text-align: center;
`

const PreviewControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 220px;
`

const HiddenFileInput = styled.input`
  display: none;
`

const UploadButton = styled.label`
  padding: 11px 20px;
  border-radius: 999px;
  border: 1px solid #4c3de3;
  background: rgba(76, 61, 227, 0.08);
  color: #4c3de3;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  text-align: center;

  :hover {
    background: rgba(76, 61, 227, 0.15);
  }
`

const RemoveImageButton = styled.button`
  padding: 11px 20px;
  border-radius: 999px;
  border: 1px solid rgba(197, 48, 48, 0.3);
  background: transparent;
  color: ${({ disabled }) => (disabled ? '#9ca5b2' : '#c53030')};
  font-weight: 700;
  font-size: 13px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  :hover {
    opacity: ${({ disabled }) => (disabled ? 1 : 0.9)};
  }
`

const PreviewHint = styled.p`
  margin: 0;
  font-size: 12px;
  color: #5b6570;
`

const FIELD_GROUPS = [
  {
    legend: 'Flight Basics',
    fields: [
      { key: 'pnr', label: 'PNR' },
      { key: 'ticket_flight_number', label: 'Flight Number' },
      { key: 'ticket_class', label: 'Class' },
      { key: 'ticket_seat_number', label: 'Seat Number' },
      { key: 'ticket_exit_row', label: 'Exit Row' },
      { key: 'ticket_designated_carrier', label: 'Designated Carrier' },
      { key: 'ticket_operating_carrier', label: 'Operating Carrier' },
      { key: 'airline_alliance', label: 'Airline Alliance' },
      { key: 'passenger_tsa_precheck', label: 'TSA Precheck' },
      { key: 'ticket_eticket_number', label: 'E-Ticket Number' },
    ],
  },
  {
    legend: 'Route & Timing',
    fields: [
      { key: 'ticket_origin', label: 'Origin (IATA)' },
      { key: 'ticket_origin_city', label: 'Origin City' },
      { key: 'ticket_destination', label: 'Destination (IATA)' },
      { key: 'ticket_destination_city', label: 'Destination City' },
      { key: 'ticket_destination_country', label: 'Destination Country' },
      { key: 'boarding_gate', label: 'Gate' },
      { key: 'boarding_zone_group', label: 'Boarding Zone/Group' },
      { key: 'boarding_date_time', label: 'Boarding Date/Time' },
      {
        key: 'boarding_departure_date_time',
        label: 'Departure Date/Time',
      },
      {
        key: 'boarding_arrival_date_time',
        label: 'Arrival Date/Time',
      },
    ],
  },
  {
    legend: 'Passenger Services',
    fields: [
      {
        key: 'ticket_special_service_request',
        label: 'Special Service Request',
      },
      { key: 'ticket_with_infant', label: 'Travelling With Infant' },
      { key: 'ticket_luggage', label: 'Baggage Allowance' },
      {
        key: 'boarding_secondary_screening',
        label: 'Secondary Screening',
      },
    ],
  },
  {
    legend: 'Loyalty & Standby',
    fields: [
      { key: 'frequent_flyer_airline', label: 'Frequent Flyer Airline' },
      { key: 'frequent_flyer_number', label: 'Frequent Flyer Number' },
      { key: 'frequent_flyer_status', label: 'Frequent Flyer Status' },
      { key: 'standby_status', label: 'Standby Status' },
      { key: 'standby_boarding_date', label: 'Standby Boarding Date/Time' },
      { key: 'standby_priority', label: 'Standby Priority' },
      { key: 'sequence_number', label: 'Sequence Number' },
    ],
  },
]

const LIST_FIELDS = [
  {
    key: 'no_board_nationalities',
    label: 'No Board Nationalities (comma separated)',
    placeholder: 'USA,CAN,DEU',
  },
  {
    key: 'no_board_passports',
    label: 'No Board Passports (comma separated)',
    placeholder: 'X1234567, AB9876543',
  },
]

const FIELD_LABEL_MAP = FIELD_GROUPS.reduce((acc, group) => {
  group.fields.forEach((field) => {
    acc[field.key] = field.label
  })
  return acc
}, {})

LIST_FIELDS.forEach((field) => {
  FIELD_LABEL_MAP[field.key] = field.label
})

FIELD_LABEL_MAP.background_image = 'Background Image'

const blankTripTemplate = {
  ticket_destination: '',
  no_board_nationalities: '',
  no_board_passports: '',
  background_image: '',
  background_image_data: '',
  background_image_url: '',
  airline_alliance: '',
  passenger_tsa_precheck: '',
  pnr: '',
  ticket_eticket_number: '',
  ticket_designated_carrier: '',
  ticket_operating_carrier: '',
  ticket_flight_number: '',
  ticket_class: '',
  ticket_seat_number: '',
  ticket_exit_row: '',
  ticket_origin: '',
  ticket_origin_city: '',
  ticket_destination_city: '',
  ticket_destination_country: '',
  ticket_special_service_request: '',
  ticket_with_infant: '',
  ticket_luggage: '',
  boarding_gate: '',
  boarding_zone_group: '',
  boarding_secondary_screening: '',
  boarding_date_time: '',
  boarding_departure_date_time: '',
  boarding_arrival_date_time: '',
  frequent_flyer_airline: '',
  frequent_flyer_number: '',
  frequent_flyer_status: '',
  standby_status: '',
  standby_boarding_date: '',
  standby_priority: '',
  sequence_number: '',
}

const OPTIONAL_TRIP_FIELDS = new Set([
  'no_board_nationalities',
  'no_board_passports',
  'background_image_data',
  'background_image_url',
])

const REQUIRED_TRIP_FIELDS = Object.keys(blankTripTemplate).filter(
  (key) => !OPTIONAL_TRIP_FIELDS.has(key)
)

const defaultTripData = {
  ...blankTripTemplate,
  ticket_destination: 'LHR',
  ticket_destination_city: 'London',
  ticket_destination_country: 'United Kingdom',
  ticket_origin: 'JFK',
  ticket_origin_city: 'New York',
  airline_alliance: 'SITA Air Alliance',
  passenger_tsa_precheck: 'Yes',
  pnr: 'ABC123',
  ticket_eticket_number: '1234567890',
  ticket_designated_carrier: 'SITA Air',
  ticket_operating_carrier: 'SITA Air',
  ticket_flight_number: 'SA100',
  ticket_class: 'Business',
  ticket_seat_number: '2A',
  ticket_exit_row: 'No',
  ticket_special_service_request: 'None',
  ticket_with_infant: 'No',
  ticket_luggage: '2 Bags',
  boarding_gate: 'B12',
  boarding_zone_group: '1',
  boarding_secondary_screening: 'No',
  boarding_date_time: '2025-11-10T14:25',
  boarding_departure_date_time: '2025-11-10T14:55',
  boarding_arrival_date_time: '2025-11-10T22:20',
  frequent_flyer_airline: 'SITA Air',
  frequent_flyer_number: 'SA1234567',
  frequent_flyer_status: 'Gold',
  standby_status: 'Confirmed',
  standby_boarding_date: '2025-11-10T13:50',
  standby_priority: 'High',
  sequence_number: '42',
  no_board_nationalities: 'IRN, PRK',
  no_board_passports: 'X1234567, AB9876543',
  background_image: '',
  background_image_data: '',
  background_image_url: '',
}

/**
 * Transform a trip object from API shape (arrays for no-board fields) into
 * a form-friendly shape (comma-separated strings) while ensuring all known
 * fields exist. Omits large inline image data from state by default.
 * @param {object|null} trip Raw trip record from config.
 * @returns {object} Form state object.
 */
function formFromTrip(trip) {
  if (!trip) {
    return { ...blankTripTemplate }
  }

  const { background_image_data, ...restTrip } = trip

  return {
    ...blankTripTemplate,
    ...restTrip,
    no_board_nationalities: (trip.no_board_nationalities || []).join(', '),
    no_board_passports: (trip.no_board_passports || []).join(', '),
    background_image_data: background_image_data || '',
  }
}

/**
 * Convert a comma-separated list field value into an array of trimmed strings.
 * Empty / falsy input returns an empty array.
 * @param {string} value Comma-delimited string.
 * @returns {string[]} Normalized list array.
 */
function normalizeList(value) {
  if (!value) {
    return []
  }
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

/**
 * Convert form state back into trip payload expected by API.
 * Trims all string values and converts list fields to arrays.
 * @param {object} form Current form state.
 * @returns {object} Trip payload suitable for POST/PUT.
 */
function tripFromForm(form) {
  const {
    id,
    no_board_nationalities,
    no_board_passports,
    background_image_url,
    background_image_data,
    ...rest
  } = form

  const normalizedRest = Object.fromEntries(
    Object.entries(rest).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.trim() : value,
    ])
  )

  return {
    ...normalizedRest,
    no_board_nationalities: normalizeList(no_board_nationalities),
    no_board_passports: normalizeList(no_board_passports),
  }
}

/**
 * Read a File object into a base64 (without prefix) string for transport.
 * @param {File} file Browser File instance.
 * @returns {Promise<string>} Base64 content string.
 */
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result === 'string') {
        const [, base64] = result.split(',')
        resolve(base64 || '')
      } else {
        resolve('')
      }
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

/**
 * Admin settings & trip management page.
 * Handles: listing trips, creating/updating/deleting trips, background image upload,
 * and toggling global restriction enforcement.
 * @param {object} props Component props.
 * @param {{trips: object[], enabled_restrictions: boolean}} props.config Current decorated config.
 * @param {(enabled: boolean) => Promise<void>} props.onToggleRestrictions Patch restriction flag.
 * @param {(options: {mode: 'create'|'update', tripId?: number, tripData: object, backgroundImage?: object, removeBackground?: boolean, previousBackgroundPath?: string}) => Promise<object|null>} props.onSaveTrip Persist trip.
 * @param {(options: {tripId: number, backgroundImagePath?: string}) => Promise<void>} props.onDeleteTrip Delete trip.
 * @param {() => void} props.onLogout Callback to leave admin mode.
 * @returns {JSX.Element}
 */
function AdminSettingsPage({
  config = { trips: [], enabled_restrictions: false },
  onToggleRestrictions,
  onSaveTrip,
  onDeleteTrip,
  onLogout,
}) {
  const [activeTripId, setActiveTripId] = useState(null)
  const [formState, setFormState] = useState(() => ({ ...blankTripTemplate }))
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const [backgroundUpload, setBackgroundUpload] = useState({
    file: null,
    previewUrl: null,
  })
  const [originalBackgroundPath, setOriginalBackgroundPath] = useState('')

  const sortedTrips = useMemo(
    () => [...(config.trips || [])].sort((a, b) => a.id - b.id),
    [config.trips]
  )

  const resetBackgroundUpload = useCallback(() => {
    setBackgroundUpload((prev) => {
      if (prev.previewUrl) {
        URL.revokeObjectURL(prev.previewUrl)
      }
      return { file: null, previewUrl: null }
    })
  }, [])

  useEffect(() => {
    return () => {
      if (backgroundUpload.previewUrl) {
        URL.revokeObjectURL(backgroundUpload.previewUrl)
      }
    }
  }, [backgroundUpload.previewUrl])

  useEffect(() => {
    if (!sortedTrips.length) {
      setActiveTripId(null)
      setFormState({ ...blankTripTemplate })
      setIsCreatingNew(true)
      setOriginalBackgroundPath('')
      resetBackgroundUpload()
      return
    }

    if (isCreatingNew) {
      setOriginalBackgroundPath('')
      return
    }

    if (activeTripId === null) {
      const firstTrip = sortedTrips[0]
      setActiveTripId(firstTrip.id)
      setFormState(formFromTrip(firstTrip))
      setOriginalBackgroundPath(firstTrip.background_image || '')
      resetBackgroundUpload()
      return
    }

    const activeTrip = sortedTrips.find((trip) => trip.id === activeTripId)
    if (activeTrip) {
      setFormState(formFromTrip(activeTrip))
      setOriginalBackgroundPath(activeTrip.background_image || '')
      resetBackgroundUpload()
    } else {
      const firstTrip = sortedTrips[0]
      setActiveTripId(firstTrip.id)
      setFormState(formFromTrip(firstTrip))
      setOriginalBackgroundPath(firstTrip.background_image || '')
      resetBackgroundUpload()
    }
  }, [sortedTrips, activeTripId, isCreatingNew, resetBackgroundUpload])

  function handleTripSelection(tripId) {
    setIsCreatingNew(false)
    setActiveTripId(tripId)
  }

  function handleAddTrip() {
    setActiveTripId(null)
    setIsCreatingNew(true)
    setFormState({ ...blankTripTemplate })
    setOriginalBackgroundPath('')
    resetBackgroundUpload()
  }

  function handleClear() {
    resetBackgroundUpload()
    setFormState({ ...blankTripTemplate })
    setOriginalBackgroundPath('')
  }

  function handleFill() {
    resetBackgroundUpload()
    setFormState({ ...defaultTripData })
    setOriginalBackgroundPath('')
  }

  function handleChange(field, value, options = {}) {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }))

    if (field === 'background_image' && !options.preserveBackgroundUpload) {
      resetBackgroundUpload()
    }
  }

  function handleBackgroundFileSelect(event) {
    const file = event.target.files && event.target.files[0]
    if (!file) {
      return
    }

    setBackgroundUpload((prev) => {
      if (prev.previewUrl) {
        URL.revokeObjectURL(prev.previewUrl)
      }
      return {
        file,
        previewUrl: URL.createObjectURL(file),
      }
    })

    handleChange('background_image', `/static/trip-images/${file.name}`, {
      preserveBackgroundUpload: true,
    })

    event.target.value = ''
  }

  function handleBackgroundRemove() {
    resetBackgroundUpload()
    setFormState((prev) => ({
      ...prev,
      background_image: '',
      background_image_data: '',
      background_image_url: '',
    }))
    setOriginalBackgroundPath('')
  }

  // Build API payload and optional background image metadata before save.
  async function handleSave() {
    const missingFields = REQUIRED_TRIP_FIELDS.filter((field) => {
      if (field === 'background_image') {
        if (isCreatingNew) {
          return !backgroundUpload.file
        }

        return (
          !backgroundUpload.file &&
          !formState.background_image &&
          !originalBackgroundPath
        )
      }

      const value = formState[field]
      if (typeof value === 'string') {
        return value.trim() === ''
      }

      return value === undefined || value === null
    })

    if (missingFields.length) {
      const fieldNames = missingFields.map(
        (field) => FIELD_LABEL_MAP[field] || field
      )
      alert(
        `Please complete the following required fields before saving:\n- ${fieldNames.join(
          '\n- '
        )}`
      )
      return
    }

    const payload = tripFromForm(formState)

    let backgroundImagePayload
    if (backgroundUpload.file) {
      try {
        const base64 = await fileToBase64(backgroundUpload.file)
        backgroundImagePayload = {
          fileName: backgroundUpload.file.name,
          mimeType: backgroundUpload.file.type,
          data: base64,
        }
      } catch (error) {
        console.error('Failed to read background image:', error)
      }
    }

    const removeBackground = Boolean(
      originalBackgroundPath &&
        !backgroundUpload.file &&
        !payload.background_image
    )

    const savedTrip = await onSaveTrip({
      mode: isCreatingNew ? 'create' : 'update',
      tripId: isCreatingNew ? undefined : activeTripId,
      tripData: payload,
      backgroundImage: backgroundImagePayload,
      removeBackground,
      previousBackgroundPath: originalBackgroundPath || undefined,
    })

    if (savedTrip) {
      setIsCreatingNew(false)
      setActiveTripId(savedTrip.id)
      setFormState(formFromTrip(savedTrip))
      setOriginalBackgroundPath(savedTrip.background_image || '')
    }

    resetBackgroundUpload()
  }

  // Convert form state + optional file into API DELETE payload.
  async function handleDelete() {
    if (activeTripId !== null) {
      const confirmed = window.confirm(
        'Are you sure you want to delete this trip? This action cannot be undone.'
      )
      if (!confirmed) {
        return
      }

      await onDeleteTrip({
        tripId: activeTripId,
        backgroundImagePath: originalBackgroundPath,
      })
      resetBackgroundUpload()
      setOriginalBackgroundPath('')
      setFormState({ ...blankTripTemplate })
      setActiveTripId(null)
      setIsCreatingNew(false)
    }
  }

  const existingBackgroundPathRaw =
    formState.background_image &&
    formState.background_image !== '/static/trip-images/'
      ? formState.background_image
      : formState.background_image_url || ''

  const existingBackgroundPath = existingBackgroundPathRaw || ''

  const computedPreview =
    backgroundUpload.previewUrl ||
    formState.background_image_data ||
    resolveAssetPath(existingBackgroundPath)

  const fileInputId = `background-upload-${
    isCreatingNew ? 'new' : activeTripId ?? 'new'
  }`

  const restrictionsEnabled = Boolean(config?.enabled_restrictions)

  return (
    <PageContainer>
      <TripListPanel>
        <GlobalSwitch $active={restrictionsEnabled}>
          <ToggleInput
            type="checkbox"
            checked={restrictionsEnabled}
            onChange={(event) => onToggleRestrictions(event.target.checked)}
          />
          <ToggleLabel $active={restrictionsEnabled}>
            Enable restrictions: {restrictionsEnabled ? 'On' : 'Off'}
          </ToggleLabel>
        </GlobalSwitch>
        <AddTripButton onClick={handleAddTrip}>+ Add Trip</AddTripButton>
        {sortedTrips.length ? (
          <TripsList>
            {sortedTrips.map((trip) => (
              <TripListItem
                key={trip.id}
                $active={!isCreatingNew && activeTripId === trip.id}
                onClick={() => handleTripSelection(trip.id)}
              >
                {trip.ticket_destination_city || 'New Destination'} ({trip.id})
                <span>
                  {trip.ticket_origin} → {trip.ticket_destination}
                </span>
              </TripListItem>
            ))}
          </TripsList>
        ) : (
          <EmptyState>No trips defined yet.</EmptyState>
        )}
      </TripListPanel>

      <FormPanel>
        <PanelHeader>
          <PanelTitle>
            {isCreatingNew ? 'New Trip' : `Trip Settings`}
          </PanelTitle>
          <PanelActions>
            <ActionButton type="button" onClick={onLogout}>
              Exit Admin
            </ActionButton>
            <ActionButton type="button" onClick={handleClear}>
              Clear Form
            </ActionButton>
            <ActionButton type="button" onClick={handleFill}>
              Fill Default
            </ActionButton>
            {!isCreatingNew && sortedTrips.length ? (
              <DeleteButton type="button" onClick={handleDelete}>
                Delete Trip
              </DeleteButton>
            ) : null}
            <ActionButton type="button" $variant="primary" onClick={handleSave}>
              Save Changes
            </ActionButton>
          </PanelActions>
        </PanelHeader>

        {!isCreatingNew && !sortedTrips.length ? (
          <EmptyState>Select a trip to begin editing.</EmptyState>
        ) : (
          <>
            {FIELD_GROUPS.map((group) => (
              <Fieldset key={group.legend}>
                <Legend>{group.legend}</Legend>
                <FieldGrid>
                  {group.fields.map((field) => (
                    <Label key={field.key} htmlFor={field.key}>
                      {field.label}
                      <Input
                        id={field.key}
                        value={formState[field.key] || ''}
                        onChange={(event) =>
                          handleChange(field.key, event.target.value)
                        }
                      />
                    </Label>
                  ))}
                </FieldGrid>
              </Fieldset>
            ))}

            <Fieldset>
              <Legend>Visuals</Legend>
              <PreviewRow>
                {computedPreview ? (
                  <PreviewImage src={computedPreview} alt="Trip background" />
                ) : (
                  <PreviewPlaceholder>No image selected</PreviewPlaceholder>
                )}
                <PreviewControls>
                  <HiddenFileInput
                    id={fileInputId}
                    type="file"
                    accept="image/*"
                    onChange={handleBackgroundFileSelect}
                  />
                  <UploadButton htmlFor={fileInputId}>
                    Choose Image
                  </UploadButton>
                  <RemoveImageButton
                    type="button"
                    onClick={handleBackgroundRemove}
                    disabled={!formState.background_image}
                  >
                    Remove Image
                  </RemoveImageButton>
                  <PreviewHint>
                    {formState.background_image
                      ? `Path: ${formState.background_image}`
                      : 'No path selected'}
                  </PreviewHint>
                  {backgroundUpload.file ? (
                    <PreviewHint>
                      New file staged: {backgroundUpload.file.name}
                    </PreviewHint>
                  ) : null}
                </PreviewControls>
              </PreviewRow>
              <FieldGrid>
                <Label htmlFor="background_image">
                  Background Image Path
                  <Input
                    id="background_image"
                    placeholder="/static/trip-images/filename.png"
                    value={formState.background_image || ''}
                    onChange={(event) =>
                      handleChange('background_image', event.target.value)
                    }
                  />
                </Label>
              </FieldGrid>
            </Fieldset>

            <Fieldset>
              <Legend>No Boarding Rules</Legend>
              <FieldGrid>
                {LIST_FIELDS.map((field) => (
                  <Label key={field.key} htmlFor={field.key}>
                    {field.label}
                    <TextArea
                      id={field.key}
                      value={formState[field.key] || ''}
                      onChange={(event) =>
                        handleChange(field.key, event.target.value)
                      }
                      placeholder={field.placeholder}
                    />
                  </Label>
                ))}
              </FieldGrid>
            </Fieldset>
          </>
        )}
      </FormPanel>
    </PageContainer>
  )
}

export default AdminSettingsPage
